import { LogOut, Settings, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/use-auth';

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const initials = user.email.slice(0, 2).toUpperCase();

  async function onLogout() {
    setOpen(false);
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="ml-1 flex size-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          className="surface absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-lg shadow-glow"
        >
          <div className="border-b border-border px-4 py-3">
            <p className="truncate text-sm font-medium">{user.email}</p>
            <div className="mt-1.5 flex items-center gap-2">
              <Badge variant="default">{user.role_label}</Badge>
              {!user.email_verified && <Badge variant="warning">Unverified</Badge>}
            </div>
          </div>
          <div className="p-1">
            <MenuItem icon={User} label="Profile" onClick={() => { setOpen(false); navigate('/settings/security'); }} />
            <MenuItem icon={Settings} label="Settings" onClick={() => { setOpen(false); navigate('/settings/security'); }} />
            <MenuItem icon={LogOut} label="Sign out" onClick={onLogout} danger />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: typeof User;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted/50 ${
        danger ? 'text-danger' : 'text-foreground'
      }`}
    >
      <Icon className="size-4" />
      {label}
    </button>
  );
}
